import React, { useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Card from '../../components/cards/Card';
import Button from '../../components/buttons/Button';
import Tab from '../../components/tabs/Tab';
import Table from '../../components/tables/Table';
import { BoxesIcon, AlertCircleIcon, Package2Icon, PenToolIcon, X, Clock, Package, Wrench, AlertTriangle } from 'lucide-react';
import ShowModal from '../../utils/ShowModal';
import { Link } from 'react-router-dom';

const InventoryDashboard = () => {
    const [inventoryStats] = useState({
        totalRawItems: 247,
        lowStockItems: 12,
        totalKits: 45,
        totalProducts: 189,
        damagedItems: 8,
        pendingReplacements: 3
    });

    // Inventory data with history
    const [inventoryData] = useState([
        {
            id: 1,
            name: 'Wire (Red)',
            category: 'Raw Material',
            currentStock: 15,
            threshold: 50,
            status: 'critical',
            lastUpdated: '2024-01-15T10:30:00',
            location: 'Shelf A-1',
            supplier: 'TechWire Co.',
            price: '₹2.50',
            history: [
                { action: 'Stock Added', quantity: 100, date: '2024-01-10T09:00:00', user: 'John Doe' },
                { action: 'Stock Used', quantity: -85, date: '2024-01-12T14:30:00', user: 'Assembly Team' },
                { action: 'Threshold Alert', quantity: 0, date: '2024-01-15T10:30:00', user: 'System' }
            ]
        },
        {
            id: 2,
            name: 'Transformer 12V',
            category: 'Raw Material',
            currentStock: 8,
            threshold: 20,
            status: 'critical',
            lastUpdated: '2024-01-14T16:45:00',
            location: 'Shelf B-3',
            supplier: 'PowerTech Ltd.',
            price: '₹45.00',
            history: [
                { action: 'Stock Added', quantity: 50, date: '2024-01-08T11:15:00', user: 'Sarah Wilson' },
                { action: 'Stock Used', quantity: -42, date: '2024-01-13T09:20:00', user: 'Assembly Team' },
                { action: 'Threshold Alert', quantity: 0, date: '2024-01-14T16:45:00', user: 'System' }
            ]
        },
        {
            id: 3,
            name: 'Controller Module',
            category: 'Raw Material',
            currentStock: 22,
            threshold: 30,
            status: 'warning',
            lastUpdated: '2024-01-15T08:15:00',
            location: 'Shelf C-2',
            supplier: 'Control Systems Inc.',
            price: '₹120.00',
            history: [
                { action: 'Stock Added', quantity: 75, date: '2024-01-05T13:45:00', user: 'Mike Chen' },
                { action: 'Stock Used', quantity: -53, date: '2024-01-14T15:30:00', user: 'Assembly Team' }
            ]
        },
        {
            id: 4,
            name: 'Solar Home Kit',
            category: 'Kit',
            currentStock: 45,
            threshold: 25,
            status: 'normal',
            lastUpdated: '2024-01-15T12:00:00',
            location: 'Warehouse 1',
            supplier: 'Internal Assembly',
            price: '₹5,500.00',
            history: [
                { action: 'Kit Assembled', quantity: 10, date: '2024-01-15T12:00:00', user: 'Assembly Team' },
                { action: 'Kit Sold', quantity: -5, date: '2024-01-14T16:20:00', user: 'Sales Team' },
                { action: 'Kit Assembled', quantity: 8, date: '2024-01-13T14:30:00', user: 'Assembly Team' }
            ]
        },
        {
            id: 5,
            name: 'Irrigation Controller',
            category: 'Product',
            currentStock: 32,
            threshold: 20,
            status: 'normal',
            lastUpdated: '2024-01-15T09:30:00',
            location: 'Warehouse 2',
            supplier: 'Internal Assembly',
            price: '₹3,200.00',
            history: [
                { action: 'Product Completed', quantity: 15, date: '2024-01-15T09:30:00', user: 'Production Team' },
                { action: 'Product Sold', quantity: -8, date: '2024-01-14T11:45:00', user: 'Sales Team' }
            ]
        },
        {
            id: 6,
            name: 'Weather Station',
            category: 'Product',
            currentStock: 18,
            threshold: 15,
            status: 'warning',
            lastUpdated: '2024-01-15T07:45:00',
            location: 'Warehouse 1',
            supplier: 'Internal Assembly',
            price: '₹2,850.00',
            history: [
                { action: 'Product Completed', quantity: 12, date: '2024-01-15T07:45:00', user: 'Production Team' },
                { action: 'Product Sold', quantity: -10, date: '2024-01-13T13:20:00', user: 'Sales Team' }
            ]
        },
        {
            id: 7,
            name: 'Smart Pump',
            category: 'Product',
            currentStock: 28,
            threshold: 25,
            status: 'normal',
            lastUpdated: '2024-01-15T11:20:00',
            location: 'Warehouse 2',
            supplier: 'Internal Assembly',
            price: '₹1,950.00',
            history: [
                { action: 'Product Completed', quantity: 20, date: '2024-01-15T11:20:00', user: 'Production Team' },
                { action: 'Product Sold', quantity: -12, date: '2024-01-14T10:15:00', user: 'Sales Team' }
            ]
        }
    ]);

    // State for tabs and sidebar
    const [activeTab, setActiveTab] = useState('all');
    const [selectedItem, setSelectedItem] = useState(null);
    const [showSidebar, setShowSidebar] = useState(false);

    // Tab configuration
    const tabsList = [
        { id: 'all', name: 'All', count: inventoryData.length },
        { id: 'product', name: 'Product', count: inventoryData.filter(item => item.category === 'Product').length },
        { id: 'raw', name: 'Raw Material', count: inventoryData.filter(item => item.category === 'Raw Material').length },
        { id: 'kit', name: 'Kit', count: inventoryData.filter(item => item.category === 'Kit').length }
    ];

    // Filter data based on active tab
    const filteredData = activeTab === 'all'
        ? inventoryData
        : inventoryData.filter(item => {
            if (activeTab === 'product') return item.category === 'Product';
            if (activeTab === 'raw') return item.category === 'Raw Material';
            if (activeTab === 'kit') return item.category === 'Kit';
            return true;
        });

    // Table headers
    const tableHeaders = [
        { key: 'name', label: 'Item Name', needFilter: true, width: '200px' },
        { key: 'category', label: 'Category', needFilter: true, width: '150px' },
        { key: 'currentStock', label: 'Current Stock', needFilter: true, width: '120px' },
        { key: 'threshold', label: 'Threshold', needFilter: true, width: '100px' },
        { key: 'status', label: 'Status', needFilter: true, width: '120px' },
        { key: 'location', label: 'Location', needFilter: true, width: '150px' },
        { key: 'lastUpdated', label: 'Last Updated', needFilter: true, width: '180px' },
        { key: 'price', label: 'Price', needFilter: true, width: '120px' }
    ];

    // Format table rows with status highlighting
    const tableRows = filteredData.map(item => ({
        ...item,
        status: (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'critical' ? 'bg-red-100 text-red-800' :
                item.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                }`}>
                {item.status === 'critical' ? 'Critical' :
                    item.status === 'warning' ? 'Low Stock' : 'Normal'}
            </span>
        ),
        lastUpdated: new Date(item.lastUpdated).toLocaleString(),
        rowBgColor: item.status === 'critical' ? 'bg-red-50' :
            item.status === 'warning' ? 'bg-yellow-50' : undefined
    }));

    // Handle row click to show sidebar
    const handleRowClick = (rowData) => {
        const originalItem = inventoryData.find(item => item.id === rowData.id);
        setSelectedItem(originalItem);
        setShowSidebar(true);
    };

    // Stats cards data
    const statsCardsData = [
        {
            title: "Total Raw Items",
            count: inventoryStats.totalRawItems,
            icon: BoxesIcon,
            bgGradient: "from-blue-50 to-blue-100",
            textColor: "text-blue-900"
        },
        {
            title: "Low Stock Alerts",
            count: inventoryStats.lowStockItems,
            icon: AlertCircleIcon,
            bgGradient: "from-red-50 to-red-100",
            textColor: "text-red-900"
        },
        {
            title: "Assembled Kits",
            count: inventoryStats.totalKits,
            icon: Package2Icon,
            bgGradient: "from-green-50 to-green-100",
            textColor: "text-green-900"
        },
        {
            title: "Product Inventory",
            count: inventoryStats.totalProducts,
            icon: PenToolIcon,
            bgGradient: "from-yellow-50 to-yellow-100",
            textColor: "text-yellow-900"
        }
    ];

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (status) {
            case 'critical':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'warning':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'success':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'error':
                return `${baseClasses} bg-red-100 text-red-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Inventory Dashboard' },
            ]} />

            <div className="flex justify-between items-center mb-6">
                <StyledHeadingText text={"Inventory Management"} size={"text-3xl"} />
                <div className="flex space-x-3">
                    <Link to="/inventory/raw-items/add" className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg">
                        Add Raw Item
                    </Link>
                    <Link to="/inventory/kit-assembly" className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg">
                        Assemble Kit
                    </Link>
                </div>
            </div>

            {/* Stats Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {statsCardsData.map((card, index) => (
                    <Card
                        key={index}
                        title={card.title}
                        count={card.count}
                        icon={card.icon}
                        bgGradient={card.bgGradient}
                        textColor={card.textColor}
                    />
                ))}
            </div>

            {/* Tabs */}
            <div className="mb-6">
                <Tab
                    tabsList={tabsList}
                    activeTab={activeTab}
                    onClick={setActiveTab}
                />
            </div>

            {/* Inventory Table */}
            <div className="relative">
                <Table
                    headers={tableHeaders}
                    rows={tableRows}
                    onRowClick={handleRowClick}
                />
            </div>

            {/* Sidebar Details */}
            {selectedItem && (
                <ShowModal
                    isOpen={showSidebar}
                    onRequestClose={() => setShowSidebar(false)}
                    modalWidth={"50%"}
                    isSidebar={true}
                    componentToPass={
                        <div className="w-full">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-semibold text-gray-800">Item Details</h2>
                            </div>
                            {/* Item Info */}
                            <div className="space-y-4 mb-6">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedItem.name}</h3>
                                    <div className="flex items-center space-x-2 mb-2">
                                        {selectedItem.category === 'Raw Material' && <Wrench size={16} className="text-blue-500" />}
                                        {selectedItem.category === 'Product' && <Package size={16} className="text-green-500" />}
                                        {selectedItem.category === 'Kit' && <Package2Icon size={16} className="text-purple-500" />}
                                        <span className="text-sm text-gray-600">{selectedItem.category}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-4 gap-4">
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-600">Current Stock</p>
                                        <p className="text-lg font-semibold text-gray-900">{selectedItem.currentStock}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-600">Threshold</p>
                                        <p className="text-lg font-semibold text-gray-900">{selectedItem.threshold}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-600">Location</p>
                                        <p className="text-lg font-semibold text-gray-900">{selectedItem.location}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-600">Price</p>
                                        <p className="text-lg font-semibold text-gray-900">{selectedItem.price}</p>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <span className={getStatusBadge(selectedItem.status)}>
                                        {selectedItem.status === 'critical' ? 'Critical Stock' :
                                            selectedItem.status === 'warning' ? 'Low Stock' : 'Normal Stock'}
                                    </span>
                                    {selectedItem.status === 'critical' && <AlertTriangle size={16} className="text-red-500" />}
                                </div>

                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-sm text-gray-600">Supplier</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedItem.supplier}</p>
                                </div>
                            </div>

                            {/* History Section */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                    <Clock size={20} className="mr-2" />
                                    Recent History
                                </h3>
                                <div className="space-y-3">
                                    {selectedItem.history.map((entry, index) => (
                                        <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-medium text-gray-900">{entry.action}</p>
                                                    <p className="text-sm text-gray-600">
                                                        {entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity} units
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(entry.date).toLocaleDateString()}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{entry.user}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>}
                />)}
        </>
    );
};

export default InventoryDashboard;